import { getAdminStats, initializeAdminStats, getCampaignHistory, getRecentActivities, logAdminActivity } from '../models/adminStatsModel';

/**
 * Fetch real-time admin statistics for dashboard
 */
export const fetchAdminStats = async () => {
  try {
    const result = await getAdminStats();
    
    if (result.success) {
      return {
        success: true,
        data: result.data
      };
    } else {
      console.error('Error fetching admin stats:', result.error);
      return {
        success: false,
        error: result.error,
        data: result.data // Return default values even on error
      };
    }
  } catch (error) {
    console.error('Error in fetchAdminStats controller:', error);
    return {
      success: false,
      error: error.message,
      data: {
        totalUsers: 0,
        wishlistEntries: 0,
        emailsSent: 0,
        conversionRate: 0,
        recentActivity: 0,
        systemHealth: 98.5,
        lastCampaignDate: null
      }
    };
  }
};

/**
 * Initialize admin stats structure (one-time setup)
 */
export const initAdminStats = async () => {
  try {
    const result = await initializeAdminStats();
    return result;
  } catch (error) {
    console.error('Error initializing admin stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Fetch campaign history for analytics
 */
export const fetchCampaignHistory = async () => {
  try {
    const result = await getCampaignHistory();
    return result;
  } catch (error) {
    console.error('Error fetching campaign history:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Fetch recent activities for dashboard
 */
export const fetchRecentActivities = async (limit = 10) => {
  try {
    const result = await getRecentActivities(limit);
    return result;
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

/**
 * Log admin activity
 */
export const logActivity = async (activityData) => {
  try {
    const result = await logAdminActivity(activityData);
    return result;
  } catch (error) {
    console.error('Error logging activity:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get dashboard metrics with real-time data
 */
export const getDashboardMetrics = async () => {
  try {
    const statsResult = await fetchAdminStats();
    
    if (statsResult.success) {
      const { data } = statsResult;
      
      // Calculate percentage changes (mock data for now, could be calculated from historical data)
      const metrics = [
        {
          title: 'Total Users',
          value: data.totalUsers.toLocaleString(),
          change: '+12.5%',
          changeType: 'positive',
          icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a4 4 0 11-8 0',
          gradient: 'from-blue-500 to-blue-600'
        },
        {
          title: 'Wishlist Entries',
          value: data.wishlistEntries.toLocaleString(),
          change: '+8.2%',
          changeType: 'positive',
          icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
          gradient: 'from-pink-500 to-pink-600'
        },
        {
          title: 'Emails Sent',
          value: data.emailsSent.toLocaleString(),
          change: '+15.3%',
          changeType: 'positive',
          icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
          gradient: 'from-green-500 to-green-600'
        },
        {
          title: 'Conversion Rate',
          value: data.conversionRate + '%',
          change: '+2.1%',
          changeType: 'positive',
          icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
          gradient: 'from-purple-500 to-purple-600'
        }
      ];
      
      return {
        success: true,
        data: {
          metrics,
          rawData: data
        }
      };
    } else {
      throw new Error(statsResult.error || 'Failed to fetch dashboard metrics');
    }
  } catch (error) {
    console.error('Error getting dashboard metrics:', error);
    return {
      success: false,
      error: error.message,
      data: {
        metrics: [],
        rawData: {
          totalUsers: 0,
          wishlistEntries: 0,
          emailsSent: 0,
          conversionRate: 0,
          recentActivity: 0,
          systemHealth: 98.5,
          lastCampaignDate: null
        }
      }
    };
  }
}; 